/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	AUTO_START_BROADCAST,
	GET_BROADCAST_SERVER,
	GET_DB_STREAM_STATE,
	GET_EVENTS_LEGACY_BROADCAST_ENDPOINT,
	GET_EVENTS_SESSION_BROADCAST_ENDPOINT,
	GET_SHOWROOMS_BROADCAST_ENDPOINT,
	MANUALLY_CREATE_BROADCAST_SERVER,
	RESET_BROADCAST_REDUCER_STATE,
	RESET_BROADCAST_SERVER,
	SET_BROADCAST_STATE,
	SET_BROADCAST_STATS,
	SET_CUSTOM_BROADCAST_ENDPOINT,
	SET_DB_STREAM_STATE,
	SET_GO_LIVE_BUTTON_CLICKED,
	SET_PREVIOUS_BROADCAST_STATE,
	SET_RECORDING_OPTION,
	SET_STREAM_STATE,
	SET_USE_CUSTOM_BROADCAST_ENDPOINT,
	START_BROADCAST,
	STOP_BROADCAST,
	SYNC_BROADCAST_REDUCER_STATE,
	UPDATE_BROADCAST_ENDPOINT,
	SET_BROADCAST_STARTING_SHOT,
	SET_BROADCAST_STARTED_BY,
	SET_CURRENT_RECORDING_STREAM_ID,
	SET_GO_LIVE_FROM_MODAL,
	SET_ADDING_DESTINATION,
	SET_EDITING_DESTINATION,
} from '../actions/broadcasting';
import DestinationFacebook from '../../images/destination-facebook.png';
import DestinationYoutube from '../../images/destination-youtube.png';
import DestinationVimeo from '../../images/destination-vimeo.png';
import DestinationLinkedIn from '../../images/destination-linkedin.png';
import DestinationOther from '../../images/destination-other.png';
import DestinationAllhands from '../../images/destination-allhands.png';
import DestinationBrandlive from '../../images/brandlive-platform-icon.png';
import DestinationMicrosoft from '../../images/destination-microsoft.png';
import DestinationZoom from '../../images/destination-zoom.png';
import { Action } from '../types/types';
import { SelectGroupOption } from '../../components/general-components/select-group';
import { CustomDestination } from './settings';

export interface CustomEndpoint {
	name: string;
	url: string;
	stream_key: string;
}

export enum BroadcastWarningTypes {
	recordingStartFailure = 'recordingStartFailure',
}

export interface BroadcastingState {
	server: any;							// server data from API side - entry in gr_broadcasts table
	loadingBroadcastServer: boolean;		// getting server data from API
	loadingBroadcastServerError: boolean;	// couldn't get server data from API
	broadcasting: boolean;					// is actively broadcasting
	startingBroadcast: boolean;				// broadcast request sent to API
	failedToStartBroadcast: boolean;		// 500 error received from API
	stoppingBroadcast: boolean;				// broadcast stop request sent to API
	failedToStopBroadcast: boolean;			// 500 error received from API
	serverState: number;					// corresponds to BROADCAST_STATES below
	streamState: number;
	previousServerState: number;			// corresponds to STREAM_STATES below
	stats: BroadcastStats;
	broadcastEndpoint: {
		url: string;
		stream_key: string;
	} | undefined,
	customEndpoint: CustomEndpoint,
	useCustomEndpoint: boolean;
	goLiveButtonClicked: boolean;
	selectedRecordingOption: number;
	broadcastStartingShotId: number;
	broadcastStartedBy: number; // gr_user
	currentRecordingStreamId: string;
	broadcastWarnings: BroadcastWarningTypes[],
	isoArchiveId?: string;
	goLiveFromModal: boolean;
	addingDestination: CustomDestination | null;
	editingDestination: string;
}

export const BROADCAST_STATES = {
	OFF: 0, 				// server has not been created for this meeting - either too early or too late
	STARTING_UP: 1, 		// server is starting up but is not yet available
	READY: 2, 				// server can be broadcast to
	BROADCAST_STARTED: 3, 	// start response received from broadcast server - it's actually live
	BROADCAST_STOPPED: 4, 	// stopped signal has been received from server
	DRAINING: 5, 			// server is actively terminating - may go unused
	KILLED: 6, 				// server is dead and unusable
	BROADCAST_STARTING: 7, 	// start signal sent to server but hasn't confirmed that it's live
	BROADCAST_FAILED: 8, 	// broadcast signal sent but the broadcast failed
	BROADCAST_PREWARMING: 9,	// server is starting a fake broadcast to sync. Random necessary step.
	BROADCAST_PREWARM_FAILED: 10,
};

export const STREAM_STATES = {
	OFF: 0,
	PRE: 1,
	MAIN: 2,
	POST: 3,
};

export const DESTINATION_TYPE = {
	BRANDLIVE: -1,
	STREAMS: 0,
	ALLHANDS: 1,
	FACEBOOK: 2,
	YOUTUBE: 3,
	VIMEO: 4,
	LINKEDIN: 5,
	OTHER: 6,
	MICROSOFT: 7,
	ZOOM: 8,
};

export const RESOLUTION_OPTIONS: SelectGroupOption[] = [
	{ label: '1080p', value: '1080p' },
	{ label: '720p', value: '720p' },
	{ label: '480p', value: '480p' },
];

export const CUSTOM_DESTINATION_OPTIONS: SelectGroupOption[] = [
	{ label: 'Brandlive', value: DESTINATION_TYPE.STREAMS, description: DestinationBrandlive },
	{ label: 'Allhands', value: DESTINATION_TYPE.ALLHANDS, description: DestinationAllhands },
	{ label: 'Facebook', value: DESTINATION_TYPE.FACEBOOK, description: DestinationFacebook },
	{ label: 'YouTube', value: DESTINATION_TYPE.YOUTUBE, description: DestinationYoutube },
	{ label: 'Vimeo', value: DESTINATION_TYPE.VIMEO, description: DestinationVimeo },
	{ label: 'LinkedIn', value: DESTINATION_TYPE.LINKEDIN, description: DestinationLinkedIn },
	{ label: 'Microsoft Teams', value: DESTINATION_TYPE.MICROSOFT, description: DestinationMicrosoft },
	{ label: 'Zoom Webinar/Events', value: DESTINATION_TYPE.ZOOM, description: DestinationZoom },
	{ label: 'Other', value: DESTINATION_TYPE.OTHER, description: DestinationOther },
];

/*
	when we open the modal, check if anyone is currently editing this
	shot, if they are, update our app with edits
*/
export interface BroadcastStats {
	bitrate: number;		// in kbps
	fps: number;			// in frames per second
	health: number;			// - 1 is good
	// - less than 1 the server is falling behind
	// - less than 0.95 frames will be lost, broadcast will buffer in video player periodically
}

export const initialBroadcastingState: BroadcastingState = {
	server: null,
	loadingBroadcastServer: false,
	loadingBroadcastServerError: false,
	broadcasting: false,
	startingBroadcast: false,
	failedToStartBroadcast: false,
	stoppingBroadcast: false,
	failedToStopBroadcast: false,
	serverState: 0,
	streamState: 2,
	previousServerState: 0,
	stats: {
		bitrate: 0,
		fps: 0,
		health: 0,
	},
	broadcastEndpoint: {
		url: '',
		stream_key: '',
	},
	customEndpoint: {
		name: '',
		url: '',
		stream_key: '',
	},
	useCustomEndpoint: false,
	goLiveButtonClicked: false,
	selectedRecordingOption: 0,
	broadcastStartingShotId: 0,
	broadcastStartedBy: 0,
	currentRecordingStreamId: '',
	broadcastWarnings: [],
	goLiveFromModal: false,
	addingDestination: null,
	editingDestination: '',
};

const actions: Map<any, Function> = new Map([
	[GET_BROADCAST_SERVER, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, loadingBroadcastServer: true }),
		failure: (pState) => ({ ...pState, loadingBroadcastServerError: true }),
		success: (pState) => {
			if (!action.payload) {
				return pState;
			}

			const newState = { ...pState };
			if ('error' in action.payload) {
				newState.serverState = 0;
			} else if ('starting_up' in action.payload) {
				newState.serverState = BROADCAST_STATES.STARTING_UP;
			} else if (action.payload.id) {
				newState.server = action.payload;
				newState.serverState = action.payload.current_state;
				newState.broadcasting = action.payload.current_state === BROADCAST_STATES.BROADCAST_STARTED;
			}

			return newState;
		},
		finish: (pState) => ({ ...pState, loadingBroadcastServer: false }),
	})],
	[START_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, startingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
		success: (pState) => {
			let broadcastWarnings = [];
			if (action.payload
				&& action.payload.broadcastWarnings) {
				broadcastWarnings = action.payload.broadcastWarnings;
			}
			return {
				...pState,
				broadcastWarnings,
				broadcasting: true,
				failedToStartBroadcast: false,
			};
		},
		finish: (pState) => ({ ...pState, startingBroadcast: false }),
	})],
	[MANUALLY_CREATE_BROADCAST_SERVER,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			start: (pState) => ({ ...pState, startingBroadcast: true }),
			failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
			success: (pState) => ({ ...pState, broadcasting: true, failedToStartBroadcast: false }),
			finish: (pState) => ({ ...pState, startingBroadcast: false }),
		}),
	],
	[STOP_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, stoppingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStopBroadcast: true }),
		success: (pState) => ({ ...pState, broadcasting: false, failedToStopBroadcast: false }),
		finish: (pState) => ({ ...pState, stoppingBroadcast: false }),
	})],
	[SET_BROADCAST_STATE, (state: BroadcastingState, action: Action) => (
		{ ...state, serverState: action.payload }
	)],
	[SET_STREAM_STATE, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, streamState: action.payload }
	)],
	[SET_PREVIOUS_BROADCAST_STATE, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, previousServerState: action.payload }
	)],
	[SET_BROADCAST_STATS, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, stats: action.payload }
	)],
	[GET_EVENTS_SESSION_BROADCAST_ENDPOINT,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			success: (pState) => {
				const newState = { ...pState };

				if (action.payload.rtmps_address) {
					newState.broadcastEndpoint = {
						url: action.payload.rtmps_address,
						stream_key: action.payload.stream_key,
					};
				}

				return newState;
			},
			failure: (pState) => ({ ...pState, broadcastEndpoint: { url: 'Error getting URL', stream_key: '' } }),
		}),
	],
	[GET_EVENTS_LEGACY_BROADCAST_ENDPOINT,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			success: (pState) => {
				const newState = { ...pState };

				if (action.payload.url) {
					newState.broadcastEndpoint = {
						url: action.payload.url,
						stream_key: action.payload.stream_key,
					};
				}

				return newState;
			},
			failure: (pState) => ({ ...pState, broadcastEndpoint: { url: 'Error getting URL', stream_key: '' } }),
		}),
	],
	[GET_SHOWROOMS_BROADCAST_ENDPOINT,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			success: (pState) => {
				let broadcastEndpoint;
				if (action.payload) {
					const broadcastConfig = action.payload.broadcastStreamingConfiguration;

					// RTMP address = rtmps://[endpoint]/[app name]/[stream key]
					// "/app" is the stream name for showrooms, usually the default
					// on most rtmp servers, but could be something different.
					broadcastEndpoint = {
						url: `rtmps://${broadcastConfig.ingestionEndpoint}/app`,
						stream_key: broadcastConfig.streamKey,
					};
				}

				return ({ ...pState, broadcastEndpoint });
			},
			failure: (pState) => ({ ...pState, broadcastEndpoint: { url: 'Error getting URL', stream_key: '' } }),
		}),
	],
	[UPDATE_BROADCAST_ENDPOINT, (state: BroadcastingState, action: Action) => (
		{ ...state, customEndpoint: action.payload, useCustomEndpoint: true }
	)],
	[SET_CUSTOM_BROADCAST_ENDPOINT, (state: BroadcastingState, action: Action) => ({
		...state,
		useCustomEndpoint: action.payload.useCustomEndpoint,
		customEndpoint: action.payload.endpoint,
	})],
	[SET_USE_CUSTOM_BROADCAST_ENDPOINT,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			success: (pState) => ({ ...pState, useCustomEndpoint: action.payload }),
		}),
	],
	[AUTO_START_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, startingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
		success: (pState) => ({ ...pState, broadcasting: true, failedToStartBroadcast: false }),
		finish: (pState) => ({ ...pState, startingBroadcast: false }),
	})],
	[SET_DB_STREAM_STATE, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, settingStreamState: true }),
		failure: (pState) => ({ ...pState, failedToSetStreamState: true }),
		success: (pState) => ({ ...pState, failedToSetStreamState: false }),
		finish: (pState) => ({ ...pState, settingStreamState: false }),
	})],
	[GET_DB_STREAM_STATE, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, gettingStreamState: true }),
		failure: (pState) => ({ ...pState, failedToGetStreamState: true }),
		success: (pState) => ({ ...pState, failedToGetStreamState: false }),
		finish: (pState) => ({
			...pState, gettingStreamState: false, streamState: action.payload.streamState,
		}),
	})],
	[SET_GO_LIVE_BUTTON_CLICKED, (state: BroadcastingState, action: Action) => (
		{ ...state, goLiveButtonClicked: action.payload }
	)],
	[RESET_BROADCAST_SERVER, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, loadingBroadcastServer: true }),
		failure: (pState) => ({ ...pState, loadingBroadcastServerError: true }),
		success: (pState) => {
			const newState = { ...pState };
			if ('error' in action.payload) {
				newState.serverState = 0;
			} else if ('starting_up' in action.payload) {
				newState.serverState = BROADCAST_STATES.STARTING_UP;
			} else if (action.payload.id) {
				newState.server = action.payload;
				newState.serverState = action.payload.current_state;
				newState.broadcasting = action.payload.current_state === BROADCAST_STATES.BROADCAST_STARTED;
			}

			return newState;
		},
		finish: (pState) => ({ ...pState, loadingBroadcastServer: false }),
	})],
	[SET_RECORDING_OPTION, (state: BroadcastingState, action: Action) => (
		{ ...state, selectedRecordingOption: action.payload }
	)],
	[SYNC_BROADCAST_REDUCER_STATE, (state: BroadcastingState, action: Action) => ({
		...state,
		...action.payload,
	})],
	[RESET_BROADCAST_REDUCER_STATE, () => (
		{ initialBroadcastingState }
	)],
	[SET_BROADCAST_STARTING_SHOT, (state: BroadcastingState, action: Action) => (
		{ ...state, broadcastStartingShotId: action.payload }
	)],
	[SET_BROADCAST_STARTED_BY, (state: BroadcastingState, action: Action) => (
		{ ...state, broadcastStartedBy: action.payload }
	)],
	[SET_CURRENT_RECORDING_STREAM_ID, (state: BroadcastingState, action: Action) => (
		{ ...state, currentRecordingStreamId: action.payload }
	)],
	[SET_GO_LIVE_FROM_MODAL, (state: BroadcastingState, action: Action) => ({
		...state,
		goLiveFromModal: action.payload,
	})],
	[SET_ADDING_DESTINATION, (state: BroadcastingState, action: Action) => (
		{ ...state, addingDestination: action.payload }
	)],
	[SET_EDITING_DESTINATION, (state: BroadcastingState, action: Action) => (
		{ ...state, editingDestination: action.payload }
	)],
]);

// eslint-disable-next-line default-param-last, max-len
export function BroadcastReducer(state: BroadcastingState = initialBroadcastingState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
